import React, { useState } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import {
  DateFormatCell,
  AmountFormatCellEmptyZero,
} from "components/common/formatter";
import styles from "./LedgerGrid.module.scss";
import { useEffect } from "react";
import { exportData } from "components/Report/exportUtility";
import { ExportConfigs } from "components/Report/exportConfigs";
import PreviousTransactionsGrid from "./MatterPreviousTransactions";
import {
  cellWithBackGround,
  createRowRender,
  RefCell,
  CenterTextHeaderCell,
  SettingsHeaderCell,
  ToolbarHeaderCell,
} from "./LedgerGridCells";
import { GridLoader } from "components/GridLoader";
import { getLedgerExportFileName } from "./ledgerExportFileName";
import { usePostingStyle } from "./usePostingStyle";

const LedgerGridContent = ({
  data,
  showDDA,
  handleActionRef,
  isPrevTrans,
  component,
  exportReport,
  matterId,
  onShowPrevTrans,
  onPrint,
  onToggleDDA,
  onToggleShowReversals,
  showDDAValue,
  showReversalsValue,
  isClosed,
  dateClosed,
  closedByUserRef,
  isExporting,
  loading,
  gridKey,
  showPreviousTransactions,
  fillAvailableHeight,
}) => {
  const gridClass = fillAvailableHeight
    ? styles.grid_container_fill
    : styles.grid_container;

  const { postingStyleMap } = usePostingStyle();

  return (
    <Grid
      key={gridKey}
      loader={
        <div style={{ height: "100%" }}>
          <GridLoader />
        </div>
      }
      showLoader={loading}
      scroll
      data={data}
      dataItemKey="postingInternalId"
      className={`h-100 grid-fs13 ${gridClass}`}
      rowRender={createRowRender(postingStyleMap)}>
      <GridNoRecords>
        {loading ? (
          <span> </span>
        ) : (
          data && data?.length === 0 && "No records available"
        )}
      </GridNoRecords>
      <GridColumn
        width="150px"
        headerCell={(props) =>
          ToolbarHeaderCell({
            ...props,
            onPrint,
            onShowPrevTrans,
            isClosed,
            dateClosed,
            closedByUserRef,
            isExporting,
            matterId,
            exportReport,
            showPreviousTransactions,
          })
        }>
        <GridColumn
          field="date"
          title="Date"
          width="100px"
          cell={DateFormatCell}
        />
        <GridColumn field="postingType" title="Type" width="60px" />
        <GridColumn
          field="reference"
          title="Ref"
          width="100px"
          cell={(props) => RefCell({ ...props, isPrevTrans }, handleActionRef)}
          className="text-wrap"
        />
        <GridColumn
          field="details"
          title="Details"
          width="310px"
          className="text-prewrap"
        />
        <GridColumn
          field="vatAmount"
          title="VAT"
          width="90px"
          headerCell={CenterTextHeaderCell}
          cell={AmountFormatCellEmptyZero}
        />
      </GridColumn>

      <GridColumn
        title="OFFICE"
        headerCell={CenterTextHeaderCell}
        headerClassName={styles.groupStart}
      >
        <GridColumn
          field="grossAmount"
          title="Debit"
          headerCell={CenterTextHeaderCell}
          headerClassName={styles.groupStart}
          className={styles.groupStart}
          cell={(e) => cellWithBackGround(e, "OFFICE", "Debit")}
          width="95px"
        />
        <GridColumn
          field="grossAmount"
          title="Credit"
          headerCell={CenterTextHeaderCell}
          cell={(e) => cellWithBackGround(e, "OFFICE", "Credit")}
          width="95px"
        />
        <GridColumn
          field="balance"
          title="Balance"
          headerCell={CenterTextHeaderCell}
          cell={(e) => cellWithBackGround(e, "OFFICE", "Amount")}
          width="100px"
        />
      </GridColumn>
      <GridColumn
        title="CLIENT"
        headerCell={CenterTextHeaderCell}
        headerClassName={styles.groupStart}
      >
        <GridColumn
          field="clientAmount"
          title="Debit"
          headerCell={CenterTextHeaderCell}
          headerClassName={styles.groupStart}
          className={styles.groupStart}
          cell={(e) => cellWithBackGround(e, "CLIENT", "Debit")}
          width="95px"
        />
        <GridColumn
          field="clientAmount"
          title="Credit"
          headerCell={CenterTextHeaderCell}
          cell={(e) => cellWithBackGround(e, "CLIENT", "Credit")}
          width="95px"
        />
        <GridColumn
          field="clientBalance"
          title="Balance"
          headerCell={CenterTextHeaderCell}
          cell={(e) => cellWithBackGround(e, "CLIENT", "Amount")}
          width="100px"
        />
      </GridColumn>
      {showDDA && (
        <GridColumn
          title="DDA"
          headerCell={CenterTextHeaderCell}
          headerClassName={styles.groupStart}
        >
          <GridColumn
            field="ddaAmount"
            title="Debit"
            headerCell={CenterTextHeaderCell}
            headerClassName={styles.groupStart}
            className={styles.groupStart}
            cell={(e) => cellWithBackGround(e, "DDA", "Debit")}
            width="95px"
          />
          <GridColumn
            field="ddaAmount"
            title="Credit"
            headerCell={CenterTextHeaderCell}
            cell={(e) => cellWithBackGround(e, "DDA", "Credit")}
            width="95px"
          />
          <GridColumn
            field="ddaBalance"
            title="Balance"
            headerCell={CenterTextHeaderCell}
            cell={(e) => cellWithBackGround(e, "DDA", "Amount")}
            width="100px"
          />
        </GridColumn>
      )}
      <GridColumn
        width="10px"
        cells={{
          headerCell: (gridHeaderProps) => (
            <SettingsHeaderCell
              {...gridHeaderProps}
              showDDAValue={showDDAValue}
              onToggleDDA={onToggleDDA}
              showReversalsValue={showReversalsValue}
              onToggleShowReversals={onToggleShowReversals}
            />
          ),
        }}></GridColumn>
    </Grid>
  );
};

export default function MatterGrid(props) {
  const [showPrevTrans, setShowPrevTrans] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [fixedScroll, setFixedScroll] = useState(props.fixedScroll);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (props?.gridData) {
      setGridData(props.gridData);
    }
  }, [props?.gridData]);

  useEffect(() => {
    setFixedScroll(props.fixedScroll);
  }, [props.fixedScroll]);

  const exportReport = async (isPreviousTransactions = false) => {
    try {
      setIsExporting(true);

      await exportData({
        ...ExportConfigs.MATTER_LEDGER,
        requestBody: ExportConfigs.MATTER_LEDGER.getRequestBody(
          props.matterId,
          isPreviousTransactions,
        ),
        fileName: getLedgerExportFileName({
          matterRef: props?.matterRef,
          isAllTab: true,
        }),
        onStart: () => setIsExporting(true),
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch (error) {
      setIsExporting(false);
    }
  };

  const wrapperClass = props.fillAvailableHeight
    ? "h-100 d-flex flex-column flex-grow-1"
    : "h-100";
  const wrapperStyle = props.fillAvailableHeight ? { minHeight: 0 } : undefined;

  return (
    <>
      <div className={wrapperClass} style={wrapperStyle}>
        <>
          <LedgerGridContent
            gridKey={props.gridKey}
            loading={props.loading}
            component={props.comp}
            fillAvailableHeight={props.fillAvailableHeight}
            data={gridData}
            showDDA={props.showDDA}
            handleActionRef={props.handleActionRef}
            isPrevTrans={false}
            matterId={props.matterId}
            onShowPrevTrans={() => {
              setShowPrevTrans(true);
            }}
            onPrint={() => {
              if (props.matterId) props.setOpenReport(true);
            }}
            onToggleDDA={() => props.setShowDDA((prev) => !prev)}
            onToggleShowReversals={() => props.setShowReversals?.((prev) => !prev)}
            showDDAValue={props.showDDA}
            showReversalsValue={props.showReversals}
            isClosed={props?.isClosed}
            dateClosed={props?.dateClosed}
            closedByUserRef={props?.closedByUserRef}
            exportReport={exportReport}
            isExporting={isExporting}
            showPreviousTransactions={true}
          />

          {showPrevTrans && (
            <PreviousTransactionsGrid
              entity={{ matterId: props.matterId, matterRef: props.matterRef }}
              title={props.prevTransTitle || "Previous Transactions"}
              isOpen={showPrevTrans}
              onClose={() => setShowPrevTrans(false)}
              showDDA={props.showDDA}
              onExport={exportReport}
              isExporting={isExporting}
              handleActionRef={props.handleActionRef}
            />
          )}
        </>
      </div>
    </>
  );
}
